import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import Table from '@/components/ui/tables/Table';
import { DynamicFiltersCard } from '@/components/ui/cards';
import type { FilterField } from '@/components/ui/cards';
import { LabelInput, Checkbox } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons';
import { Text } from '@/components/ui/text/Text';
import { useGeolocalizationPage } from '@/hooks/checkInLocation';


export const GeolocalizationPage: FC = () => {
  const { t: translate } = useTranslation('checkInLocation');
  const {
    locationId,
    locationName,
    locationAddress,
    generateGeolocation,
    onToggleGenerateGeolocation,
    rows,
    columns,
    actions,
    isLoading,
    latitude,
    longitude,
    onLatitudeChange,
    onLongitudeChange,
    onAdd,
    onPreview,
    isAdding,
    radius,
    onRadiusChange,
    onGenerate,
    isGenerating,
  } = useGeolocalizationPage();

  const modeSpecificFields: FilterField[] = generateGeolocation
    ? [
      {
        id: 'radius',
        type: 'input',
        component: (
          <LabelInput
            label={translate('geolocalizacion.form.radius')}
            value={radius}
            onChange={onRadiusChange}
            placeholder={translate('geolocalizacion.form.radiusPlaceholder')}
            data-testid="radius-input"
          />
        ),
      },
      {
        id: 'generate-button',
        type: 'custom',
        component: (
          <div className="flex items-end pb-1">
            <PermissionGuard action="edit">
              <PrimaryButton
                onClick={() => void onGenerate()}
                disabled={isGenerating || !radius}
                data-testid="generate-geolocation-button"
              >
                {translate('geolocalizacion.buttons.generate')}
              </PrimaryButton>
            </PermissionGuard>
          </div>
        ),
      },
    ]
    : [
      {
        id: 'latitude',
        type: 'input',
        component: (
          <LabelInput
            label={translate('geolocalizacion.form.latitude')}
            value={latitude}
            onChange={onLatitudeChange}
            placeholder={translate('geolocalizacion.form.latitudePlaceholder')}
            data-testid="latitude-input"
          />
        ),
      },
      {
        id: 'longitude',
        type: 'input',
        component: (
          <LabelInput
            label={translate('geolocalizacion.form.longitude')}
            value={longitude}
            onChange={onLongitudeChange}
            placeholder={translate('geolocalizacion.form.longitudePlaceholder')}
            data-testid="longitude-input"
          />
        ),
      },
      {
        id: 'add-button',
        type: 'custom',
        component: (
          <div className="flex items-end pb-1">
            <PermissionGuard action="edit">
              <PrimaryButton
                onClick={() => void onAdd()}
                disabled={isAdding || !latitude || !longitude}
                data-testid="add-geolocation-button"
              >
                {translate('geolocalizacion.buttons.add')}
              </PrimaryButton>
            </PermissionGuard>
          </div>
        ),
      },
    ];

  const filterFields: FilterField[] = [
    {
      id: 'id',
      type: 'input',
      component: (
        <LabelInput
          label={translate('geolocalizacion.locationInfo.id')}
          value={String(locationId)}
          readOnly
        />
      ),
    },
    {
      id: 'name',
      type: 'input',
      component: (
        <LabelInput
          label={translate('geolocalizacion.locationInfo.name')}
          value={locationName}
          readOnly
          inputClassName="truncate"
        />
      ),
    },
    {
      id: 'address',
      type: 'input',
      component: (
        <LabelInput
          label={translate('geolocalizacion.locationInfo.address')}
          value={locationAddress ?? '—'}
          readOnly
          inputClassName="truncate"
        />
      ),
    },
    {
      id: 'generate',
      type: 'custom',
      component: (
        <div className="flex items-center gap-2 pb-2">
          <Text variant="subheader-regular">
            {translate('geolocalizacion.generateGeolocation')}
          </Text>
          <Checkbox
            checked={generateGeolocation}
            onChange={onToggleGenerateGeolocation}
          />
        </div>
      ),
    },
    ...modeSpecificFields,
    {
      id: 'preview-button',
      type: 'custom',
      component: (
        <div
          className="flex items-end pb-1"
          title={rows.length > 4
            ? translate('geolocalizacion.buttons.previewDisabledTitle')
            : undefined}
        >
          <PrimaryButton
            onClick={onPreview}
            disabled={rows.length > 4}
            data-testid="preview-geolocation-button"
          >
            {translate('geolocalizacion.buttons.preview')}
          </PrimaryButton>
        </div>
      ),
    },
  ];

  return (
    <PageLayout isLoading={isLoading}>
      <div className="flex flex-col gap-6" data-testid="geolocalizacion-page">
        <DynamicFiltersCard
          fields={filterFields}
        />

        <Table
          columns={columns}
          data={rows}
          totalRows={rows.length}
          actions={actions}
          data-testid="geolocalizacion-table"
        />
      </div>
    </PageLayout>
  );
};

export default GeolocalizationPage;
